import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, AlertTriangle, X, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TrialExpiryBanner
 * -----------------
 * Pulls the caller's org info (plan + trial_days_left) and renders a banner:
 *   - plan=trial && days_left > 1   → friendly green "X days of Pro left"
 *   - plan=trial && days_left <= 1  → amber urgency "Trial ends tomorrow"
 *   - plan=free                     → red "Trial ended — upgrade to keep Pro features"
 *   - plan=pro / business           → null (banner hidden)
 *
 * The banner is dismissable per session (sessionStorage). It re-appears on
 * the next session login, so users on day 6 of a trial don't miss it.
 */
const TrialExpiryBanner = () => {
  const navigate = useNavigate();
  const [orgInfo, setOrgInfo] = useState(null);
  const [dismissed, setDismissed] = useState(
    sessionStorage.getItem('trial_banner_dismissed') === '1'
  );

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/me/org`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setOrgInfo(r.data);
      } catch {
        // swallow — banner is purely informational
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!orgInfo || dismissed) return null;
  const plan = orgInfo.plan;
  if (plan !== 'trial' && plan !== 'free') return null;
  // v32: suppress for personal/demo accounts — public demo viewers should never
  // see a "Trial Ended" message (it's confusing on a marketing-style demo).
  // Personal orgs auto-spawn with `is_personal: true` for every fresh demo login.
  if (orgInfo.is_personal) return null;

  const daysLeft = orgInfo.trial_days_left;
  const isExpired = plan === 'free';
  const isUrgent = plan === 'trial' && (daysLeft === null || daysLeft <= 1);

  // Tone-based palette — solid gradient backgrounds so text stays
  // readable regardless of page bg (was bg-rose-500/10 → invisible on
  // light pages because the 10% tint washed out to near-white).
  const palette = isExpired
    ? {
        bg: 'bg-rose-600 bg-gradient-to-r from-rose-600 via-rose-500 to-pink-600 border-rose-700/50',
        icon: AlertTriangle,
        iconColor: 'text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]',
      }
    : isUrgent
    ? {
        bg: 'bg-amber-500 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 border-amber-600/50',
        icon: AlertTriangle,
        iconColor: 'text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]',
      }
    : {
        bg: 'bg-emerald-600 bg-gradient-to-r from-emerald-600 via-teal-500 to-cyan-600 border-emerald-700/50',
        icon: Sparkles,
        iconColor: 'text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]',
      };
  const Icon = palette.icon;

  // Headline + subtext
  let headline = '';
  let sub = '';
  let cta = 'Upgrade to Pro';
  if (isExpired) {
    headline = 'Your trial has ended';
    sub = 'Pro features (anomaly detection, AI Advisor, forecasts) are paused. Upgrade to reactivate.';
    cta = 'Upgrade now';
  } else if (isUrgent) {
    headline = 'Your trial ends in less than 24 hours';
    sub = 'Upgrade today to keep your dashboards, alerts, and history. $199/mo, cancel any time.';
    cta = 'Upgrade to Pro';
  } else if (daysLeft != null && daysLeft <= 3) {
    headline = `${daysLeft} day${daysLeft === 1 ? '' : 's'} of Pro trial left`;
    sub = 'Time to lock in your team\'s savings — annual plan saves you $398/yr.';
    cta = 'See plans';
  } else if (daysLeft != null) {
    headline = `${daysLeft} days of Pro trial remaining`;
    sub = 'Connect a cloud account to see real anomalies + recommendations.';
    cta = 'See plans';
  } else {
    headline = 'You\'re on a Pro trial';
    sub = 'Connect your first cloud to start seeing real savings.';
    cta = 'See plans';
  }

  return (
    <div
      className={`border-b ${palette.bg} shadow-lg`}
      data-testid="trial-expiry-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
        <Icon className={`w-5 h-5 shrink-0 ${palette.iconColor}`} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-white drop-shadow-sm">{headline}</div>
          <div className="text-xs text-white/90 truncate sm:whitespace-normal">{sub}</div>
        </div>
        <Button
          size="sm"
          onClick={() => navigate('/pricing')}
          className="bg-white text-slate-900 hover:bg-slate-100 font-semibold shadow-md hover:shadow-lg transition-shadow"
          data-testid="trial-banner-cta-btn"
        >
          {cta}
          <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
        </Button>
        <button
          onClick={() => {
            sessionStorage.setItem('trial_banner_dismissed', '1');
            setDismissed(true);
          }}
          className="text-white/70 hover:text-white p-1 transition-colors"
          aria-label="Dismiss banner"
          data-testid="trial-banner-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default TrialExpiryBanner;
